import { useMemo, useState, type ReactNode } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Logo } from '@/components/ui/Logo/Logo';
import { Toast } from '@/components/ui/Toast/Toast';
import { useOnboardingForm } from './useOnboardingForm';
import { StepProgress } from './components/StepProgress';
import { ProfilePhotoStep } from './steps/ProfilePhotoStep';
import { AgeStep } from './steps/AgeStep';
import { SkillsStep } from './steps/SkillsStep';
import { AboutStep } from './steps/AboutStep';
import { CertificatesStep } from './steps/CertificatesStep';
import { SizeStep } from './steps/SizeStep';

const TOTAL_STEPS = 6;

type OnboardingStep = {
  title: string;
  node: ReactNode;
  /** Blocks Next and hides Skip until `canAdvance` is true. */
  required?: boolean;
  canAdvance?: boolean;
};

export function OnboardingPage() {
  const form = useOnboardingForm();
  const [step, setStep] = useState(0);

  const stepDef = useMemo<OnboardingStep[]>(
    () => [
      { title: 'Add a profile picture', node: <ProfilePhotoStep form={form} /> },
      {
        title: 'How old are you?',
        node: <AgeStep form={form} />,
        required: true,
        canAdvance: Boolean(form.dateOfBirth),
      },
      { title: 'What are your skills?', node: <SkillsStep form={form} /> },
      { title: 'Tell us about yourself', node: <AboutStep form={form} /> },
      { title: 'Got any certificates?', node: <CertificatesStep form={form} /> },
      { title: 'Pick your t-shirt size', node: <SizeStep form={form} /> },
    ],
    [form],
  );

  const isLast = step === TOTAL_STEPS - 1;
  const isFirst = step === 0;
  const current = stepDef[step];

  // A required step holds the flow until it has a value. The buttons below are
  // disabled/hidden accordingly, but the handlers re-check so the gate survives
  // a click that bypasses the disabled state.
  const blocked = Boolean(current.required && !current.canAdvance);

  const goNext = async () => {
    if (blocked) return;
    if (isLast) {
      await form.finish();
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goSkip = async () => {
    if (blocked) return;
    if (isLast) {
      await form.finish();
      return;
    }
    setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goBack = () => {
    if (isFirst) return;
    setStep((s) => Math.max(s - 1, 0));
  };

  return (
    <Flex
      minH="100vh"
      bg="white"
      direction="column"
      align="center"
      justify="flex-start"
      px={{ base: '4', md: '6' }}
      py={{ base: '6', md: '10' }}
    >
      <Flex direction="column" align="center" gap="4" w="full" maxW="520px">
        <Logo />
        <Text fontSize="sm" color="volyx.textSecondary">
          Step {step + 1} of {TOTAL_STEPS}
        </Text>
        <StepProgress total={TOTAL_STEPS} current={step} />

        <Box
          mt="4"
          w="full"
          bg="white"
          borderRadius="2xl"
          p={{ base: '5', md: '6' }}
          boxShadow="0 8px 24px rgba(0,0,0,0.08)"
        >
          <Text as="h2" fontSize="xl" fontWeight="semibold" color="volyx.primary" mb="4">
            {current.title}
          </Text>
          <Box>{current.node}</Box>
        </Box>

        <Flex w="full" justify="space-between" align="center" mt="2" gap="3">
          <chakra.button
            type="button"
            px="5"
            py="3"
            borderRadius="xl"
            fontWeight="semibold"
            cursor={isFirst ? 'not-allowed' : 'pointer'}
            border="none"
            bg="volyx.lightGray"
            color="volyx.text"
            opacity={isFirst ? 0.4 : 1}
            transition="opacity 0.2s, background-color 0.2s"
            _hover={!isFirst ? { bg: 'volyx.mediumGray' } : undefined}
            onClick={goBack}
            disabled={isFirst || form.submitting}
          >
            Back
          </chakra.button>
          <Flex gap="3">
            {!current.required ? (
              <chakra.button
                type="button"
                px="5"
                py="3"
                borderRadius="xl"
                fontWeight="semibold"
                cursor="pointer"
                border="none"
                bg="transparent"
                color="volyx.textSecondary"
                transition="color 0.2s, background-color 0.2s"
                _hover={{ color: 'volyx.primary', bg: 'volyx.lightGray' }}
                onClick={goSkip}
                disabled={form.submitting}
              >
                Skip
              </chakra.button>
            ) : null}
            <chakra.button
              type="button"
              px="6"
              py="3"
              borderRadius="xl"
              fontWeight="semibold"
              cursor="pointer"
              border="none"
              bg="volyx.action"
              color="white"
              transition="transform 0.2s, background-color 0.2s, opacity 0.2s"
              _hover={{ bg: 'rgba(0,122,255,0.9)', transform: 'translateY(-1px)' }}
              _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
              onClick={goNext}
              disabled={form.submitting || blocked}
            >
              {form.submitting ? 'Saving...' : isLast ? 'Finish' : 'Next'}
            </chakra.button>
          </Flex>
        </Flex>
      </Flex>

      {form.toast ? (
        <Toast
          message={form.toast.message}
          type={form.toast.type}
          onClose={form.hideToast}
        />
      ) : null}
    </Flex>
  );
}
